import React, { useEffect, useState, useContext, useCallback } from 'react';
import {
  Typography, Tag, Spin, Table, Card, Row, Col, Alert, Button, Space,
  Drawer, Form, Input, InputNumber, Select, Switch, DatePicker, Divider, Checkbox,
  message, Popconfirm, Avatar, Tooltip, Modal, Radio, Descriptions,
} from 'antd';
import {
  TrophyOutlined, EditOutlined,
  SendOutlined, DeleteOutlined, EditFilled,
} from '@ant-design/icons';
import { useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';
import { useBackFallback, usePageLabel } from '../components/BackChip';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const STATUS_COLOR = { draft: 'default', active: 'green', completed: 'blue', canceled: 'red' };
const MEDAL = { 1: '🥇', 2: '🥈', 3: '🥉' };
const STATUSES = ['draft', 'active', 'completed', 'canceled'];

// Race bar colours — 10 distinct, ordered by prominence
const RACE_COLORS = [
  '#e8682a', // 1st  orange-brand
  '#d4a017', // 2nd  gold
  '#8b5e3c', // 3rd  bronze
  '#2563eb', // 4th  blue
  '#7c3aed', // 5th  purple
  '#059669', // 6th  teal
  '#dc2626', // 7th  red
  '#0891b2', // 8th  cyan
  '#c026d3', // 9th  magenta
  '#4f46e5', // 10th indigo
];

function fmt(v, metric) {
  if (v == null) return '-';
  if (metric && (metric === 'AHT' || metric === 'ACW' || metric === 'Agent Talk')) {
    const total = Math.round(v);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }
  if (metric === 'Avg Score') return `${typeof v === 'number' ? v.toFixed(1) : v}%`;
  if (typeof v === 'number') return v % 1 === 0 ? String(v) : v.toFixed(2);
  return String(v);
}

function scoreSuffix(win) {
  if (win === '% of Team above Goal') return '%';
  if (win === '% Cheers per Inbound Calls') return '%';
  if (win === '% of Errors vs Inbound Calls') return '%';
  if (win === 'Issues per Team Size') return ' / person';
  return '';
}

function fmtImprovement(delta, metric) {
  if (delta == null) return '—';
  const abs = fmt(Math.abs(delta), metric);
  return delta >= 0 ? `+${abs}` : `-${abs}`;
}

const TIME_METRICS = new Set(['AHT', 'ACW', 'Agent Talk']);

// The rankings list is not always the whole field: an engagement can be
// configured to show only the top N, or only those above goal. Heading and note
// say which, so a truncated list is never mistaken for the full standings.
function rankingsHeading(eng) {
  if (!eng) return { title: 'Full Rankings', note: null };
  if (eng.WinCondition === 'Above Goal' && eng.ShowOnlyAboveGoal) {
    return {
      title: 'Above Goal',
      note:  'Showing only participants who met the goal. Those below it are hidden by this engagement’s settings.',
    };
  }
  const n = Number(eng.ShowTopN || 0);
  if (n > 0) {
    return {
      title: `Top ${n}`,
      note:  `Showing the top ${n} by rank. Everyone is scored and ranked against the whole field; ties at the cut are all shown, so this list can run slightly longer than ${n}.`,
    };
  }
  return { title: 'Full Rankings', note: null };
}

function fmtImprovementPct(v) {
  if (v == null) return '—';
  return v >= 0 ? `+${v.toFixed(1)}%` : `${v.toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Olympic Podium
// ---------------------------------------------------------------------------
function OlympicPodium({ entries, metric, win }) {
  const qualified = entries.filter((e) => e.Score != null && !e.UnderMin);

  const byRank = {};
  qualified.forEach((e) => {
    const r = e.Rank;
    if (!byRank[r]) byRank[r] = [];
    byRank[r].push(e);
  });

  const rank1 = byRank[1] || [];
  const rank2 = rank1.length < 3 ? (byRank[2] || []) : [];
  const rank3 = (rank1.length + rank2.length) < 3 ? (byRank[3] || []) : [];

  const slots = [
    { rank: 2, slotEntries: rank2 },
    { rank: 1, slotEntries: rank1 },
    { rank: 3, slotEntries: rank3 },
  ];

  const standH   = { 1: 130, 2: 96, 3: 72 };
  const standClr = { 1: 'linear-gradient(180deg,#f59e0b 0%,#d97706 100%)',
                     2: 'linear-gradient(180deg,#cbd5e1 0%,#94a3b8 100%)',
                     3: 'linear-gradient(180deg,#e0a060 0%,#b07840 100%)' };
  const cardBg   = { 1: 'rgba(255,255,255,0.92)', 2: 'rgba(255,255,255,0.88)', 3: 'rgba(255,255,255,0.88)' };
  const cardBorder = { 1: '2px solid #f59e0b', 2: '1px solid #cbd5e1', 3: '1px solid #e8ab75' };
  const ordinal  = { 1: '1st', 2: '2nd', 3: '3rd' };
  const ordClr   = { 1: '#d97706', 2: '#64748b', 3: '#a16207' };

  return (
    <div style={{
      position: 'relative',
      borderRadius: 16,
      overflow: 'hidden',
      marginBottom: 40,
      background: `
        radial-gradient(ellipse 80% 40% at 50% 0%, rgba(147,197,253,0.55) 0%, transparent 70%),
        radial-gradient(ellipse 100% 60% at 20% 30%, rgba(196,181,253,0.25) 0%, transparent 60%),
        radial-gradient(ellipse 100% 60% at 80% 30%, rgba(253,186,116,0.20) 0%, transparent 60%),
        linear-gradient(180deg,
          #dbeafe 0%,
          #e0f2fe 18%,
          #f0f9ff 35%,
          #fefce8 55%,
          #fef9f0 70%,
          #fff7ed 100%)
      `,
      padding: '8px 0 0',
    }}>
      {/* Stadium crowd rows */}
      {[0, 1, 2, 3].map((i) => (
        <div key={i} style={{
          position: 'absolute',
          top: 8 + i * 14,
          left: 0, right: 0,
          height: 10,
          background: i % 2 === 0
            ? 'repeating-linear-gradient(90deg, rgba(99,102,241,0.08) 0px, rgba(99,102,241,0.08) 8px, transparent 8px, transparent 16px)'
            : 'repeating-linear-gradient(90deg, rgba(239,68,68,0.07) 0px, rgba(239,68,68,0.07) 8px, transparent 8px, transparent 16px)',
          pointerEvents: 'none',
        }} />
      ))}

      {/* Olympic rings watermark */}
      <div style={{
        position: 'absolute', top: 10, right: 24,
        display: 'flex', gap: 4, opacity: 0.18, pointerEvents: 'none',
      }}>
        {['#3b82f6','#f59e0b','#000','#16a34a','#dc2626'].map((c, i) => (
          <div key={i} style={{
            width: 22, height: 22, borderRadius: '50%',
            border: `3px solid ${c}`,
            marginLeft: i > 0 ? -8 : 0,
          }} />
        ))}
      </div>

      {/* Title */}
      <div style={{ textAlign: 'center', paddingTop: 56, paddingBottom: 4, position: 'relative' }}>
        <Text style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
          textTransform: 'uppercase', color: 'rgba(30,58,138,0.5)',
        }}>
          Podium
        </Text>
      </div>

      {/* The three slots */}
      <Row justify="center" gutter={16} style={{ alignItems: 'flex-end', padding: '0 24px', position: 'relative' }}>
        {slots.map(({ rank, slotEntries }) => {
          if (!slotEntries.length) return <Col key={rank} span={7} />;
          const score = slotEntries[0]?.Score;
          const fontSize = rank === 1 ? 15 : 13;
          return (
            <Col key={rank} span={7} style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: 6 }}>
                <Text style={{ fontSize: rank === 1 ? 40 : 30 }}>{MEDAL[rank]}</Text>
              </div>
              <Card
                size="small"
                style={{
                  background: cardBg[rank],
                  border: cardBorder[rank],
                  borderRadius: 12,
                  boxShadow: rank === 1
                    ? '0 8px 24px rgba(217,119,6,0.25)'
                    : '0 4px 12px rgba(0,0,0,0.10)',
                }}
              >
                {slotEntries.length > 3 ? (
                  <Text strong style={{ fontSize }}>{slotEntries.length} tied</Text>
                ) : (
                  slotEntries.map((e, i) => (
                    <Text key={e.UserID ?? e.TeamID ?? i} strong style={{ display: 'block', fontSize }}>
                      {e.DisplayName || e.TeamName || '-'}
                    </Text>
                  ))
                )}
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                  <Text strong style={{ fontSize: 12 }}>
                    {score == null ? '—' : `${fmt(score, metric)}${scoreSuffix(win)}`}
                  </Text>
                  {slotEntries.length === 1 && slotEntries[0].AboveCount != null && slotEntries[0].TotalCount != null
                    ? ` (${slotEntries[0].AboveCount}/${slotEntries[0].TotalCount})`
                    : ''}
                </Text>
                {slotEntries.length === 1 && slotEntries[0].ParticipantCount != null && (
                  <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
                    {slotEntries[0].ParticipantCount} participants
                  </Text>
                )}
              </Card>
              {/* ordinal label */}
              <div style={{
                marginTop: 8, fontWeight: 800, fontSize: 11,
                color: ordClr[rank], letterSpacing: '0.10em', textTransform: 'uppercase',
              }}>
                {ordinal[rank]}
              </div>
              {/* stand block */}
              <div style={{
                height: standH[rank],
                background: standClr[rank],
                borderRadius: '6px 6px 0 0',
                boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.3)',
                marginTop: 6,
              }} />
            </Col>
          );
        })}
      </Row>

      {/* Floor stripe */}
      <div style={{ height: 8, background: 'rgba(0,0,0,0.06)' }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Horse Race
// ---------------------------------------------------------------------------
function HorseRace({ entries, metric, win, teamColumn, heading }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 60);
    return () => clearTimeout(t);
  }, []);

  const isImprovement = win === 'Monthly Improvement';
  const isLowerBetter = !isImprovement && (metric === 'AHT' || metric === 'ACW' || metric === 'Agent Talk');

  const raceEntries = [...entries]
    .filter((e) => e.Score != null)
    .sort((a, b) => (a.Rank ?? 999) - (b.Rank ?? 999))
    .slice(0, 10);

  const scores = raceEntries.map((e) => e.Score);
  const maxScore = Math.max(...scores);
  const minScore = Math.min(...scores);

  const barPct = (score) => {
    if (isLowerBetter || isImprovement) {
      if (maxScore === minScore) return 100;
      const ratio = isLowerBetter
        ? (maxScore - score) / (maxScore - minScore)
        : (score - minScore) / (maxScore - minScore);
      return Math.round(ratio * 80 + 20);
    }
    if (maxScore === 0) return 0;
    return Math.round((score / maxScore) * 80 + 20);
  };

  return (
    <div>
      <style>{`
        @keyframes lanePop {
          from { opacity: 0; transform: translateX(-12px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* Track container */}
      <div style={{
        background: 'linear-gradient(180deg, #1c1917 0%, #292524 100%)',
        borderRadius: 16,
        padding: '20px 28px 24px',
        marginBottom: 32,
        boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
        overflow: 'hidden',
        position: 'relative',
      }}>
        {/* Track title */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20,
        }}>
          <Text style={{
            color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.18em', textTransform: 'uppercase',
          }}>
            Top {raceEntries.length}
          </Text>
          {(isLowerBetter || isImprovement) && (
            <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>
              {isImprovement ? 'Higher % improvement = further ahead' : 'Lower score = further ahead'}
            </Text>
          )}
        </div>

        {raceEntries.map((entry, i) => {
          const pct   = barPct(entry.Score);
          const color = RACE_COLORS[i % RACE_COLORS.length];
          const isFirst = entry.Rank === 1;
          return (
            <div
              key={entry.UserID ?? entry.TeamID ?? i}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
                animation: `lanePop 0.35s ease-out ${i * 0.06}s both`,
              }}
            >
              {/* Rank badge */}
              <div style={{
                width: 36, flexShrink: 0, textAlign: 'right',
                fontSize: i < 3 ? 18 : 13,
                fontWeight: 700, lineHeight: 1,
              }}>
                {MEDAL[entry.Rank] || (
                  <span style={{ color: 'rgba(255,255,255,0.45)' }}>#{entry.Rank}</span>
                )}
              </div>

              {/* Name */}
              <div style={{
                width: 140, flexShrink: 0,
                color: isFirst ? '#fde68a' : 'rgba(255,255,255,0.82)',
                fontSize: 13, fontWeight: isFirst ? 700 : 500,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {entry.DisplayName || entry.TeamName || '-'}
                {teamColumn && entry.TeamName && entry.DisplayName && (
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 1 }}>
                    {entry.TeamName}
                  </div>
                )}
              </div>

              {/* Bar track */}
              <div style={{
                flex: 1, height: 32, background: 'rgba(255,255,255,0.06)',
                borderRadius: 6, overflow: 'hidden', position: 'relative',
              }}>
                {/* Lane dashes */}
                {[25, 50, 75].map((pctMark) => (
                  <div key={pctMark} style={{
                    position: 'absolute', top: 0, bottom: 0,
                    left: `${pctMark}%`, width: 1,
                    background: 'rgba(255,255,255,0.06)',
                  }} />
                ))}
                {/* Bar */}
                <div style={{
                  height: '100%',
                  width: ready ? `${pct}%` : '0%',
                  background: `linear-gradient(90deg, ${color}cc, ${color})`,
                  borderRadius: 6,
                  transition: `width ${0.7 + i * 0.04}s cubic-bezier(0.22, 1, 0.36, 1) ${i * 0.07}s`,
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                  paddingRight: 10, boxSizing: 'border-box',
                  boxShadow: `0 0 12px ${color}66`,
                }}>
                  <Text style={{
                    color: '#fff', fontSize: 12, fontWeight: 700,
                    whiteSpace: 'nowrap', textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                  }}>
                    {isImprovement
                      ? (TIME_METRICS.has(metric) ? fmtImprovementPct(entry.Score) : fmtImprovement(entry.Score, metric))
                      : `${fmt(entry.Score, metric)}${scoreSuffix(win)}`}
                  </Text>
                </div>
              </div>

              {/* Finish flag for 1st */}
              {isFirst && (
                <Text style={{ fontSize: 16, flexShrink: 0 }}>🏁</Text>
              )}
            </div>
          );
        })}
      </div>

      <Title level={5} style={{ marginBottom: heading?.note ? 2 : undefined }}>
        {heading?.title ?? 'Full Rankings'}
      </Title>
      {heading?.note && (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
          {heading.note}
        </Typography.Paragraph>
      )}
      <RankedTable entries={entries} metric={metric} win={win} teamColumn={teamColumn} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ranked table
// ---------------------------------------------------------------------------
function RankedTable({ entries, metric, win, teamColumn }) {
  const hasUnderMin = entries.some((e) => e.UnderMin);
  const isImprovement = win === 'Monthly Improvement';
  const cols = [
    { title: '#', dataIndex: 'Rank', key: 'rank', width: 50,
      render: (v) => MEDAL[v] || v || '-' },
    ...(teamColumn ? [{ title: 'Team', dataIndex: 'TeamName', key: 'team', width: 160 }] : []),
    { title: 'Name', dataIndex: 'DisplayName', key: 'name',
      render: (v, r) => (
        <Space size={6}>
          <span>{v || r.TeamName || '-'}</span>
          {r.UnderMin && <Tag color="warning" style={{ fontSize: 11, marginInlineEnd: 0 }}>Under Min</Tag>}
        </Space>
      ),
    },
    ...(isImprovement ? [
      { title: 'Start', dataIndex: 'StartVal', key: 'start', width: 90,
        render: (v, r) => <Text style={r.UnderMin ? { color: '#8c8c8c' } : undefined}>{fmt(v, metric)}</Text> },
      { title: 'End', dataIndex: 'EndVal', key: 'end', width: 90,
        render: (v, r) => <Text style={r.UnderMin ? { color: '#8c8c8c' } : undefined}>{fmt(v, metric)}</Text> },
      { title: 'Improvement', dataIndex: 'Score', key: 'score', width: 120,
        render: (v, r) => (
          <Text strong style={r.UnderMin ? { color: '#8c8c8c' } : v > 0 ? { color: '#16a34a' } : v < 0 ? { color: '#dc2626' } : undefined}>
            {TIME_METRICS.has(metric) ? fmtImprovementPct(v) : fmtImprovement(v, metric)}
          </Text>
        ),
      },
    ] : [
      { title: 'Score', dataIndex: 'Score', key: 'score', width: 110,
        render: (v, r) => (
          <Text strong style={r.UnderMin ? { color: '#8c8c8c' } : undefined}>
            {v == null ? '—' : `${fmt(v, metric)}${scoreSuffix(win)}`}
            {r.AboveCount != null && r.TotalCount != null ? ` (${r.AboveCount}/${r.TotalCount})` : ''}
          </Text>
        ),
      },
    ]),
    ...(entries.some((e) => e.ParticipantCount != null) ? [{
      title: 'Participants', dataIndex: 'ParticipantCount', key: 'pcount', width: 130,
      render: (v, r) => (
        <span>
          {v}
          {r.UnderMinCount > 0 && <Text type="secondary" style={{ fontSize: 11 }}> ({r.UnderMinCount} under min)</Text>}
        </span>
      ),
    }] : []),
    { title: '', key: 'spacer', render: () => null },
  ];
  return (
    <>
      <Table
        size="small"
        rowKey={(r, i) => `${r.UserID ?? r.TeamID ?? i}`}
        dataSource={entries}
        columns={cols}
        pagination={false}
        rowClassName={(r) => r.Rank === 1 ? 'leaderboard-gold' : r.UnderMin ? 'leaderboard-undermin' : ''}
      />
      {hasUnderMin && (
        <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
          <Tag color="warning" style={{ fontSize: 11 }}>Under Min</Tag> = did not meet the minimum qualifying count
        </Text>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Above Goal view (replaces Podium + Race for 'Above Goal' win condition)
// ---------------------------------------------------------------------------
function AboveGoalView({ entries, metric, goal, teamColumn, win }) {
  const above = entries.filter((e) => e.AboveGoal && !e.UnderMin);
  const below  = entries.filter((e) => !e.AboveGoal && !e.UnderMin);
  const underMin = entries.filter((e) => e.UnderMin);
  const isImprovement = win === 'Monthly Improvement';

  const cols = [
    ...(teamColumn ? [{ title: 'Team', dataIndex: 'TeamName', key: 'team', width: 160 }] : []),
    { title: 'Name', dataIndex: 'DisplayName', key: 'name',
      render: (v, r) => v || r.TeamName || '-' },
    ...(isImprovement ? [
      { title: 'Start', dataIndex: 'StartVal', key: 'start', width: 90, render: (v) => fmt(v, metric) },
      { title: 'End',   dataIndex: 'EndVal',   key: 'end',   width: 90, render: (v) => fmt(v, metric) },
      { title: 'Improvement', dataIndex: 'Score', key: 'score', width: 120,
        render: (v) => <Text strong style={v > 0 ? { color: '#16a34a' } : v < 0 ? { color: '#dc2626' } : undefined}>{TIME_METRICS.has(metric) ? fmtImprovementPct(v) : fmtImprovement(v, metric)}</Text> },
    ] : [
      { title: 'Score', dataIndex: 'Score', key: 'score', width: 120,
        render: (v) => <Text strong>{v == null ? '—' : fmt(v, metric)}</Text> },
    ]),
    { title: '', key: 'spacer', render: () => null },
  ];

  const rowKey = (r, i) => `${r.UserID ?? r.TeamID ?? i}`;

  return (
    <div>
      {/* Summary banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        border: '1px solid #bbf7d0', borderRadius: 12, padding: '16px 24px', marginBottom: 28,
      }}>
        <div style={{ fontSize: 36 }}>🎯</div>
        <div>
          <Text strong style={{ fontSize: 18, color: '#15803d', display: 'block' }}>
            {above.length} / {entries.filter((e) => !e.UnderMin).length} above goal
          </Text>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Goal: {goal == null ? '—' : fmt(goal, metric)}
          </Text>
        </div>
        {underMin.length > 0 && (
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 'auto' }}>
            {underMin.length} participant{underMin.length !== 1 ? 's' : ''} under minimum count
          </Text>
        )}
      </div>

      {/* Above goal section */}
      {above.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: '#16a34a', color: '#fff', borderRadius: '8px 8px 0 0',
            padding: '6px 16px', fontWeight: 700, fontSize: 13,
          }}>
            <span>✓</span> Above Goal ({above.length})
          </div>
          <Table
            size="small"
            rowKey={rowKey}
            dataSource={above}
            columns={cols}
            pagination={false}
            style={{ border: '2px solid #16a34a', borderTop: 'none', borderRadius: '0 8px 8px 8px' }}
            rowClassName={() => 'above-goal-row'}
          />
        </div>
      )}

      {/* Below goal section */}
      {below.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: '#6b7280', color: '#fff', borderRadius: '8px 8px 0 0',
            padding: '6px 16px', fontWeight: 700, fontSize: 13,
          }}>
            <span>—</span> Below Goal ({below.length})
          </div>
          <Table
            size="small"
            rowKey={rowKey}
            dataSource={below}
            columns={cols}
            pagination={false}
            style={{ border: '2px solid #d1d5db', borderTop: 'none', borderRadius: '0 8px 8px 8px', opacity: 0.85 }}
          />
        </div>
      )}

      {/* Under min section */}
      {underMin.length > 0 && (
        <div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
            <Tag color="warning">Under Min</Tag> did not meet the minimum qualifying count
          </Text>
          <Table
            size="small"
            rowKey={rowKey}
            dataSource={underMin}
            columns={cols}
            pagination={false}
            style={{ opacity: 0.65 }}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comments section
// ---------------------------------------------------------------------------
function CommentsSection({ engId, currentUser }) {
  const [comments, setComments] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [text,     setText]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [editText,   setEditText]   = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/api/engagements/${engId}/comments`)
      .then((r) => setComments(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [engId]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/api/engagements/${engId}/comments`, { comment: text.trim() });
      setText('');
      load();
    } catch {
      message.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSave = async (cid) => {
    if (!editText.trim()) return;
    try {
      await api.put(`/api/engagements/${engId}/comments/${cid}`, { comment: editText.trim() });
      setEditingId(null);
      load();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Edit failed');
    }
  };

  const handleDelete = async (cid) => {
    try {
      await api.delete(`/api/engagements/${engId}/comments/${cid}`);
      load();
    } catch {
      message.error('Delete failed');
    }
  };

  const isAdmin = currentUser?.Role === 'admin';

  return (
    <Card size="small" title="Comments" style={{ marginTop: 32 }}>
      {loading ? <Spin size="small" /> : (
        comments.length === 0
          ? <Text type="secondary">No comments yet.</Text>
          : comments.map((c) => (
            <div key={c.ID} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <Avatar size={32} style={{ background: '#e8682a', flexShrink: 0 }}>
                  {(c.AuthorName || '?')[0].toUpperCase()}
                </Avatar>
                <div style={{ flex: 1 }}>
                  <Space size={4}>
                    <Text strong style={{ fontSize: 13 }}>{c.AuthorName}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {c.CreatedAt ? new Date(c.CreatedAt + 'Z').toLocaleString() : ''}
                    </Text>
                  </Space>
                  {editingId === c.ID ? (
                    <div style={{ marginTop: 6 }}>
                      <Input.TextArea
                        rows={3}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        style={{ maxWidth: 560 }}
                      />
                      <Space style={{ marginTop: 6 }}>
                        <Button size="small" type="primary" onClick={() => handleEditSave(c.ID)}>Save</Button>
                        <Button size="small" onClick={() => setEditingId(null)}>Cancel</Button>
                      </Space>
                    </div>
                  ) : (
                    <Paragraph style={{ marginBottom: 0, marginTop: 2, whiteSpace: 'pre-wrap' }}>
                      {c.Comment}
                    </Paragraph>
                  )}
                </div>
                {editingId !== c.ID && (
                  <Space size={4}>
                    {c.UserID === currentUser?.ID && (
                      <Tooltip title="Edit">
                        <Button
                          type="text" size="small" icon={<EditFilled />}
                          onClick={() => { setEditingId(c.ID); setEditText(c.Comment); }}
                        />
                      </Tooltip>
                    )}
                    {(isAdmin || c.UserID === currentUser?.ID) && (
                      <Popconfirm title="Delete comment?" onConfirm={() => handleDelete(c.ID)}>
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    )}
                  </Space>
                )}
              </div>
            </div>
          ))
      )}

      <Divider style={{ margin: '12px 0' }} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <Input.TextArea
          rows={2}
          placeholder="Add a comment..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          style={{ flex: 1, maxWidth: 560 }}
        />
        <Button
          type="primary" icon={<SendOutlined />}
          loading={submitting}
          disabled={!text.trim()}
          onClick={handleSubmit}
        >
          Post
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Edit drawer
// ---------------------------------------------------------------------------
// Also opened from the Edit button on Admin > Engagements, the only place
// an engagement is edited from.
export function EditDrawer({ open, onClose, eng, allUsers, allCampaigns, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [teamCampaigns, setTeamCampaigns] = useState({});
  const status     = eng?.Status;
  const isTerminal = status === 'completed' || status === 'canceled';
  const canEditDates = status === 'draft' || status === 'active';
  const isCallsEngagement = ['Calls', 'Inbound Calls', 'Inbound Contacts', 'Evaluations', 'Errors'].includes(eng?.Object);

  useEffect(() => {
    if (open && eng) {
      form.setFieldsValue({
        title:                eng.Name,
        description:          eng.Description || '',
        status:               eng.Status,
        hide_from_agents:     eng.HideFromAgents || false,
        leaderboard_type:     eng.LeaderboardType ?? 0,
        viewer_user_ids:      eng.ViewerUserIds || [],
        start_month:          eng.StartMonth ? dayjs(eng.StartMonth, 'YYYY-MM') : null,
        end_month:            eng.EndMonth   ? dayjs(eng.EndMonth,   'YYYY-MM') : null,
        excluded_user_ids:    eng.ExcludedUserIds || [],
        min_qualifying_count: eng.MinQualifyingCount || 0,
      });
      const tc = {};
      (eng.Teams || []).forEach((t) => {
        tc[t.TeamID] = (eng.TeamCampaigns || {})[t.TeamID] || [];
      });
      setTeamCampaigns(tc);
    }
  }, [open, eng, form]);

  const doSave = async (v) => {
    setSaving(true);
    try {
      const payload = {
        title:            v.title,
        description:      v.description || '',
        hide_from_agents: v.hide_from_agents || false,
        leaderboard_type: v.leaderboard_type ?? 0,
        ...(isTerminal ? {} : {
          status:          v.status,
          viewer_user_ids: v.viewer_user_ids || [],
        }),
        update_dates: canEditDates,
        ...(canEditDates ? {
          start_month:          v.start_month ? dayjs(v.start_month).format('YYYY-MM') : null,
          end_month:            v.end_month   ? dayjs(v.end_month).format('YYYY-MM')   : null,
          excluded_user_ids:    v.excluded_user_ids || [],
          min_qualifying_count: v.min_qualifying_count || 0,
          ...(isCallsEngagement ? { team_campaigns: teamCampaigns } : {}),
        } : {}),
      };
      await api.put(`/api/engagements/${eng.ID}`, payload);
      message.success('Saved');
      onSaved();
      onClose();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const fieldsToValidate = isTerminal ? ['title'] : ['title', 'status'];
    try { await form.validateFields(fieldsToValidate); } catch { return; }
    const v = form.getFieldsValue();
    const newStatus = v.status;
    const isTerminalTransition = !isTerminal && (newStatus === 'completed' || newStatus === 'canceled');
    if (isTerminalTransition) {
      const isCompleting = newStatus === 'completed';
      Modal.confirm({
        title: isCompleting ? 'Complete Engagement?' : 'Cancel Engagement?',
        content: isCompleting
          ? 'This will lock in the final scores and close the engagement. This cannot be undone.'
          : 'This will cancel the engagement. No scores will be saved. This cannot be undone.',
        okText: 'Yes, confirm',
        okType: isCompleting ? 'primary' : 'danger',
        cancelText: 'Go back',
        onOk: () => doSave(v),
      });
      return;
    }
    doSave(v);
  };

  return (
    <Drawer
      title="Edit Engagement"
      open={open}
      onClose={onClose}
      width={560}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>Save</Button>
        </div>
      }
    >
      {/* Read-only summary of locked fields */}
      {eng && (
        <>
          <Descriptions
            size="small"
            column={1}
            bordered
            style={{ marginBottom: 20 }}
            items={[
              { key: 'model',  label: 'Competition Model', children: eng.Model },
              { key: 'obj',    label: 'Object',            children: eng.Object },
              { key: 'metric', label: 'Metric',            children: eng.Metric },
              { key: 'win',    label: 'Win Condition',     children: eng.WinCondition },
              ...(eng.GoalValue != null ? [{
                key: 'goal',
                label: 'Goal',
                children: fmt(eng.GoalValue, eng.Metric),
              }] : []),
              { key: 'period', label: 'Period',            children: `${eng.StartMonth} – ${eng.EndMonth}` },
              ...(eng.Teams?.length ? [{
                key: 'teams',
                label: 'Teams',
                children: eng.Teams.map((t) => t.TeamName).join(', '),
              }] : []),
            ]}
          />
          <Divider style={{ margin: '0 0 20px' }} />
        </>
      )}

      <Form form={form} layout="vertical">
        <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Required' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={4} />
        </Form.Item>
        {isTerminal ? (
          <Form.Item label="Status">
            <Tag color={STATUS_COLOR[status]}>{status.charAt(0).toUpperCase() + status.slice(1)}</Tag>
            <span style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>Cannot be changed</span>
          </Form.Item>
        ) : (
          <>
            <Form.Item name="status" label="Status" rules={[{ required: true }]}>
              <Select>
                {STATUSES.map((s) => (
                  <Option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="viewer_user_ids" label="Additional Viewers">
              <Select mode="multiple" optionFilterProp="children" placeholder="Users who can view">
                {(allUsers || []).map((u) => (
                  <Option key={u.ID} value={u.ID}>{u.DisplayName}</Option>
                ))}
              </Select>
            </Form.Item>
          </>
        )}
        <Form.Item name="leaderboard_type" label="Leaderboard View">
          <Radio.Group>
            <Radio.Button value={0}>🏆 Podium</Radio.Button>
            <Radio.Button value={1}>🏇 Horse Race</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="hide_from_agents" label="Hide from Agents" valuePropName="checked">
          <Switch />
        </Form.Item>

        {canEditDates && (
          <>
            <Divider>Draft / Active only</Divider>
            <Space>
              <Form.Item name="start_month" label="Start Month">
                <DatePicker picker="month" format="YYYY-MM" />
              </Form.Item>
              <Form.Item name="end_month" label="End Month">
                <DatePicker picker="month" format="YYYY-MM" />
              </Form.Item>
            </Space>
            {(eng?.Object === 'Calls' || eng?.Object === 'Evaluations') && (
              <Form.Item
                name="min_qualifying_count"
                label={`Minimum ${eng?.Object === 'Evaluations' ? 'Evaluations' : 'Calls'}`}
                extra="Participants below this count are flagged; ranked at the bottom when completed."
              >
                <InputNumber min={0} style={{ width: 160 }} placeholder="0 = no minimum" />
              </Form.Item>
            )}
            {isCallsEngagement && (eng?.Teams || []).length > 0 && (
              <>
                <Divider orientation="left" style={{ margin: '8px 0' }}>Campaign Assignments</Divider>
                {(eng?.Teams || []).map((t) => (
                  <Form.Item
                    key={t.TeamID}
                    label={`${t.TeamName} -- Campaigns`}
                    extra="Leave empty to include all campaigns for this team."
                    style={{ marginBottom: 8 }}
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="All campaigns"
                      style={{ maxWidth: 420 }}
                      optionFilterProp="children"
                      value={teamCampaigns[t.TeamID] || []}
                      onChange={(vals) => setTeamCampaigns((prev) => ({ ...prev, [t.TeamID]: vals }))}
                    >
                      {(allCampaigns || []).map((c) => (
                        <Option key={c.ID} value={c.ID}>{c.Name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                ))}
              </>
            )}
            <Form.Item
              name="excluded_user_ids"
              label="Excluded Participants"
              extra="Select participants to exclude from scoring."
            >
              <Select mode="multiple" optionFilterProp="children" placeholder="None excluded">
                {(allUsers || []).map((u) => (
                  <Option key={u.ID} value={u.ID}>{u.DisplayName}</Option>
                ))}
              </Select>
            </Form.Item>
          </>
        )}
      </Form>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
/**
 * One engagement's standings.
 *
 * Takes its id from the route normally, or from a prop when it is rendered
 * somewhere that is not a route -- the Campaign Summary shows it in a panel,
 * where navigating away would throw away the campaign, team and window the
 * reader had applied. In panel mode the back button goes, since there is
 * nothing to go back to.
 */
export default function EngagementLeaderboardPage({ engagementId = null, inPanel = false }) {
  const { id: routeId } = useParams();
  const id = engagementId ?? routeId;
  const location     = useLocation();
  const currentUser  = useContext(UserContext);

  const [data,    setData]    = useState(null);
  // Names this page in Recent and on the next page's Back chip.
  usePageLabel(!inPanel && data?.engagement?.Name ? `Engagement: ${data.engagement.Name}` : null);
  // The Back chip (Layout): where you came from, else the engagements list
  // (none in a panel).
  const mine = location.pathname.startsWith('/my-engagements');
  useBackFallback(inPanel ? null : mine ? '/my-engagements' : '/admin/engagements',
    mine ? 'My Engagements & Contests' : 'All Engagements');
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(() => {
    return api.get(`/api/engagements/${id}/leaderboard`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Failed to load'));
  }, [id]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  if (loading) return <Spin style={{ display: 'block', marginTop: 80 }} />;
  if (error)   return <Alert type="error" message={error} />;

  const eng    = data.engagement;
  const model  = data.model;
  const metric = eng.Metric;
  const win    = eng.WinCondition;
  const role    = currentUser?.Role;
  const isAdmin = role === 'admin' || role === 'qa_admin';

  const hasEntries = (data.entries || []).length > 0;
  const hasTeams   = (data.teams  || []).some((t) => (t.entries || []).length > 0);

  const isAboveGoalMode = win === 'Above Goal';
  const isHorseRace     = !isAboveGoalMode && eng.LeaderboardType === 1;
  const heading         = rankingsHeading(eng);

  // Render one leaderboard section (podium, race, or above-goal view)
  const renderSection = (entries, showTeamCol) => {
    if (!entries.length) return null;
    if (isAboveGoalMode) {
      return <AboveGoalView entries={entries} metric={metric} goal={eng.GoalValue} teamColumn={showTeamCol} win={win} />;
    }
    return isHorseRace
      ? <HorseRace  entries={entries} metric={metric} win={win} teamColumn={showTeamCol} heading={heading} />
      : (
        <>
          <OlympicPodium entries={entries} metric={metric} win={win} />
          <Title level={5} style={{ marginBottom: heading.note ? 2 : undefined }}>{heading.title}</Title>
          {heading.note && (
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
              {heading.note}
            </Typography.Paragraph>
          )}
          <RankedTable   entries={entries} metric={metric} win={win} teamColumn={showTeamCol} />
        </>
      );
  };

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <TrophyOutlined style={{ fontSize: 24, color: '#e8682a' }} />
          <Title level={3} style={{ margin: 0 }}>{eng.Name}</Title>
          <Tag color={STATUS_COLOR[eng.Status]}>{eng.Status}</Tag>
        </div>
        <Space style={{ marginTop: 8, color: 'rgba(0,0,0,0.72)', fontSize: 13 }} wrap>
          <span>{eng.Model}</span>
          <span style={{ color: '#bbb' }}>|</span>
          <span>{eng.Object} - {metric}</span>
          <span style={{ color: '#bbb' }}>|</span>
          <span>{win}</span>
          <span style={{ color: '#bbb' }}>|</span>
          <span>{eng.StartMonth} to {eng.EndMonth}</span>
          {eng.GoalValue != null && (
            <>
              <span style={{ color: '#bbb' }}>|</span>
              <span>Goal: {fmt(eng.GoalValue, metric)}</span>
            </>
          )}
          {eng.MinQualifyingCount > 0 && (
            <>
              <span style={{ color: '#bbb' }}>|</span>
              <span>Min {eng.Object === 'Evaluations' ? 'Evals' : eng.Object || 'Calls'}: {eng.MinQualifyingCount}</span>
            </>
          )}
        </Space>
        {eng.Description && (
          <Paragraph style={{ marginTop: 12, maxWidth: 720, color: 'rgba(0,0,0,0.88)' }}>
            {eng.Description}
          </Paragraph>
        )}
      </div>

      {data.note === 'mock_mode' && (
        <Alert type="info" message="Scoring unavailable in mock mode (no AgentMonthlyStats data)" style={{ marginBottom: 24 }} />
      )}


      {model === 'Team vs Team' && hasEntries && renderSection(data.entries, false)}

      {model === 'Individual' && hasEntries && renderSection(data.entries, true)}

      {model === 'Individuals per Team' && (data.teams || []).map((team) => (
        <div key={team.TeamID} style={{ marginBottom: 48 }}>
          <Title level={5}>{team.TeamName}</Title>
          {team.PctAboveGoal != null && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              {team.AboveCount} / {team.TotalCount} above goal ({team.PctAboveGoal}%)
            </Text>
          )}
          {(team.entries || []).length > 0
            ? renderSection(team.entries, false)
            : <Text type="secondary">No scored participants.</Text>
          }
        </div>
      ))}

      {!hasEntries && !hasTeams && data.note !== 'mock_mode' && (
        <Alert type="warning" message="No scored participants found. Check that the engagement period, participants, and campaigns are configured correctly." />
      )}

      <CommentsSection engId={parseInt(id, 10)} currentUser={currentUser} />

    </>
  );
}
